import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, UploadCloud } from "lucide-react";
import { BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { QuickPublishVideoDialog } from "./QuickPublishVideoDialog";

const resolveVideo = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

// Native <video> for /api/files/ uploads AND any direct mp4/webm/mov URL;
// iframe path is reserved for embed providers (YouTube/Vimeo).
const isSelfHosted = (url) => {
  if (!url) return false;
  if (/\/api\/files\//.test(url)) return true;
  return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
};

const ROLE_LABEL = {
  artist: "Artist",
  storyteller: "Storyteller",
  journalist: "Journalist",
  writer: "Writer",
};

/**
 * "In their own words" — homepage editorial carousel.
 *
 * Layout: one large featured player on the left + a list of up-to-3 thumbnails
 * on the right. Clicking a thumbnail swaps it into the featured slot. Falls
 * back to a YouTube/Vimeo iframe for non self-hosted videos.
 *
 * When the section is empty AND the viewer is an admin, the section still
 * renders with an editorial empty state inviting a one-click upload — so the
 * gallery is never silently missing from the homepage.
 */
export const IntroVideosSection = ({ items = [], onChanged }) => {
  const [active, setActive] = useState(0);
  const [publishOpen, setPublishOpen] = useState(false);
  const videoRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Autoplay (muted) the featured clip whenever the active item changes so the
  // hero feels alive — admins can still pause via the standard controls.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.load();
    el.play().catch(() => {});
  }, [active]);

  // Empty state — only admins see the section so they can populate it.
  if (!items || items.length === 0) {
    if (!isAdmin) return null;
    return (
      <section
        data-testid="intro-videos-section"
        className="bg-[#0F1320] text-white px-6 md:px-12 lg:px-24 py-20"
      >
        <div className="max-w-3xl">
          <p className="label-eyebrow text-[#F4C430]">— Admin only · homepage gallery</p>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mt-2 text-white">
            No intro videos yet
          </h2>
          <p className="text-white/70 mt-3 max-w-xl">
            Add the first one — a 30-second studio clip is plenty. Once published, this
            section turns into &ldquo;Meet the masters&rdquo; for every visitor.
          </p>
          <button
            type="button"
            data-testid="intro-videos-quick-publish-empty"
            onClick={() => setPublishOpen(true)}
            className="mt-8 inline-flex items-center gap-2 bg-[#F4C430] text-[#0F1320] px-6 py-4 label-eyebrow hover:bg-white transition-colors"
          >
            <UploadCloud className="w-4 h-4" />
            Publish a video in 30 seconds
          </button>
        </div>
        <QuickPublishVideoDialog
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
          onPublished={() => onChanged?.()}
        />
      </section>
    );
  }

  const current = items[active] || items[0];
  const more = items.slice(0, 4).filter((_, idx) => idx !== active);
  const currentUrl = resolveVideo(current.video_url);

  return (
    <section
      data-testid="intro-videos-section"
      className="bg-[#0F1320] text-white px-6 md:px-12 lg:px-24 py-20"
    >
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-[#F4C430]">— In their own words</p>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mt-2 text-white">
            Meet the masters
          </h2>
          <p className="text-white/70 mt-2 max-w-xl">
            Hear directly from the artists, storytellers, and journalists shaping the African art
            scene — short introductions filmed in the studio.
          </p>
        </div>
        <Link
          to="/artists"
          data-testid="intro-videos-all-artists"
          className="label-eyebrow border border-[#F4C430] text-[#F4C430] hover:bg-[#F4C430] hover:text-[#0F1320] transition-colors px-5 py-3"
        >
          Browse artists →
        </Link>
        {isAdmin && (
          <button
            type="button"
            data-testid="intro-videos-quick-publish"
            onClick={() => setPublishOpen(true)}
            className="label-eyebrow bg-[#F4C430] text-[#0F1320] hover:bg-white transition-colors px-5 py-3 inline-flex items-center gap-2"
            title="Admin only · one-click homepage publish"
          >
            <UploadCloud className="w-3.5 h-3.5" />
            Add video
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Featured player */}
        <div className="lg:col-span-8" data-testid="intro-video-featured">
          <div className="aspect-video bg-black border border-white/10 overflow-hidden">
            {isSelfHosted(current.video_url) ? (
              <video
                key={current.id}
                ref={videoRef}
                data-testid="intro-video-player"
                src={currentUrl}
                controls
                playsInline
                muted
                preload="metadata"
                className="w-full h-full object-cover"
              >
                <track kind="captions" />
              </video>
            ) : (
              <iframe
                key={current.id}
                src={currentUrl}
                title={current.title || current.artist_name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
          <div className="mt-5 flex items-end justify-between gap-4">
            <div>
              <p className="label-eyebrow text-[#F4C430]">
                — {ROLE_LABEL[current.artist_role] || "Artist"}
                {current.artist_country ? `  ·  ${current.artist_country}` : ""}
              </p>
              <h3 className="font-serif-display text-3xl md:text-4xl mt-2 text-white">
                {current.artist_name}
              </h3>
              {current.title && current.title !== current.artist_name && (
                <p className="text-white/80 mt-1 text-base">{current.title}</p>
              )}
              {current.artist_bio && (
                <p className="text-white/60 mt-3 text-sm max-w-xl line-clamp-2">
                  {current.artist_bio}
                </p>
              )}
            </div>
            <Link
              to={`/artists/${current.artist_id}`}
              data-testid={`intro-video-visit-artist-${current.artist_id}`}
              className="label-eyebrow text-white/80 hover:text-[#F4C430] whitespace-nowrap"
            >
              Visit profile →
            </Link>
          </div>
        </div>

        {/* Thumbnail rail */}
        <div className="lg:col-span-4 flex flex-col gap-4" data-testid="intro-video-rail">
          {more.map((item) => {
            const idx = items.findIndex((x) => x.id === item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActive(idx)}
                data-testid={`intro-video-thumb-${item.id}`}
                className="group relative text-left border border-white/10 bg-black/60 hover:border-[#F4C430] transition-colors overflow-hidden"
              >
                <div className="aspect-video bg-black">
                  {item.artist_avatar_url ? (
                    <img
                      src={
                        item.artist_avatar_url.startsWith("/api")
                          ? `${BACKEND_URL}${item.artist_avatar_url}`
                          : item.artist_avatar_url
                      }
                      alt={item.artist_name ? `${item.artist_name} — introduction video` : "African artist introduction video"}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1E3A8A]/60 to-[#0F1320]" />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-[#F4C430] text-[#0F1320] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                </div>
                <div className="px-3 py-2.5">
                  <p className="label-eyebrow text-[#F4C430]">
                    {ROLE_LABEL[item.artist_role] || "Artist"}
                  </p>
                  <p className="font-serif-display text-base text-white mt-1 truncate">
                    {item.artist_name}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <QuickPublishVideoDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onPublished={() => onChanged?.()}
      />
    </section>
  );
};
